import type { IFile, IFileModel } from '@/types/globalTypes'

export const NewsCategory = (json: INewsCategory): INewsCategoryModel => {
	const fromJSON = (json: INewsCategory) => {
		const { id, language, name, slug } = json
		return {
			id,
			language: language?.toLowerCase(),
			name,
			slug,
		}
	}

	return { ...fromJSON(json) }
}

export const NewsTranslation = (json: INewsTranslation): INewsTranslation => {
	return {
		id: Number(json.id),
		language: json.language?.toUpperCase(),
		title: json.title,
		slug: json.slug,
	}
}

export const News = (json: INews): INewsModel => {
	const fromJSON = (json: INews) => {
		const {
			meta_canonical,
			meta_title,
			meta_description,
			meta_robots,
			language,
			created_at,
			updated_at,
			published_at,
			files,
			slug,
			categories,
			translations,
			author,
			...rest
		} = json
		return {
			...rest,
			metaCanonical: meta_canonical,
			metaTitle: meta_title,
			metaDescription: meta_description,
			metaRobots: meta_robots,
			slug,
			categories: categories ? categories.map((category) => NewsCategory(category)) : [],
			language: language?.toUpperCase(),
			createdAt: created_at,
			updatedAt: updated_at,
			publishedAt: published_at ?? null,
			files: files ? files.map((file) => FileModel(file)) : [],
			translations: translations ? translations.map((translation) => NewsTranslation(translation)) : [],
			author: author ? Author(author) : null,
			authorId: author?.id ?? null,
		}
	}

	return { ...fromJSON(json) }
}

export const newsToJSON = (obj: Partial<INewsModel>): Partial<INewsArguments> => {
	// author is read-only on the news resource - only author_id is sent
	const {
		metaCanonical,
		metaTitle,
		metaDescription,
		metaRobots,
		language,
		createdAt,
		updatedAt,
		publishedAt,
		files,
		categories,
		translationOf,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		author,
		authorId,
		...rest
	} = obj

	return {
		...rest,
		meta_canonical: metaCanonical,
		meta_title: metaTitle,
		meta_description: metaDescription,
		meta_robots: metaRobots,
		category_ids: categories?.length ? categories.map((category) => category.id) : undefined,
		language: language?.toLowerCase(),
		created_at: createdAt,
		updated_at: updatedAt,
		published_at: publishedAt ?? undefined,
		translation_of: translationOf ?? undefined,
		// backend clears the author when author_id is missing, so null is sent explicitly
		author_id: authorId ?? null,
		files: files ? [] : [],
	}
}

export const FileModel = (json: IFile): IFileModel => {
	const fromJSON = (json: IFile) => {
		const { created_at, updated_at, full_path, model_id, model_type, ...rest } = json
		return {
			...rest,
			createdAt: created_at,
			updatedAt: updated_at,
			fullPath: full_path,
			modelId: model_id,
			modelType: model_type,
		}
	}

	return { ...fromJSON(json) }
}

export const FileFromJSON = (json: IFile): IFileModel => {
	const fromJSON = (json: IFile) => {
		const { created_at, updated_at, full_path, model_id, model_type, ...rest } = json
		return {
			...rest,
			createdAt: created_at,
			updatedAt: updated_at,
			fullPath: full_path,
			modelId: model_id,
			modelType: model_type,
		}
	}

	return { ...fromJSON(json) }
}
